import { Document, Page, Text, View, StyleSheet, pdf, Image } from '@react-pdf/renderer'
import type { Company, SeparationListItem } from '../types'

const fmtDate = (iso: string) => new Date(iso).toLocaleDateString('pt-BR')

function makeStyles(primaryColor: string) {
  return StyleSheet.create({
    page: { padding: 36, fontSize: 10, fontFamily: 'Helvetica', color: '#111' },
    header: { borderBottomWidth: 2, borderBottomColor: primaryColor, borderBottomStyle: 'solid', paddingBottom: 12, marginBottom: 18 },
    brand: { fontSize: 18, fontWeight: 700, color: primaryColor },
    subtitle: { fontSize: 9, color: '#666', marginTop: 2 },
    meta: { flexDirection: 'row', justifyContent: 'space-between', marginBottom: 14 },
    metaCol: { flexGrow: 1, paddingRight: 12 },
    metaLabel: { fontSize: 8, color: '#666', textTransform: 'uppercase' },
    metaValue: { fontSize: 11, fontWeight: 700, marginTop: 2 },
    table: { borderTopWidth: 1, borderTopColor: '#888', borderTopStyle: 'solid', borderBottomWidth: 1, borderBottomColor: '#888', borderBottomStyle: 'solid', marginTop: 10 },
    rowHead: { flexDirection: 'row', backgroundColor: primaryColor, padding: 6 },
    headText: { color: '#fff', fontSize: 9, fontWeight: 700 },
    row: { flexDirection: 'row', padding: 6, borderBottomWidth: 0.5, borderBottomColor: '#ddd', borderBottomStyle: 'solid' },
    rowAlt: { backgroundColor: '#F7F7F7' },
    cCheck: { width: '6%', flexShrink: 0, fontSize: 9 },
    cSku: { width: '18%', flexShrink: 0, fontSize: 9 },
    cName: { width: '34%', flexShrink: 0, fontSize: 9 },
    cQty: { width: '10%', flexShrink: 0, textAlign: 'right', fontSize: 9, fontWeight: 700 },
    cPrio: { width: '12%', flexShrink: 0, textAlign: 'right', fontSize: 9 },
    cStock: { width: '10%', flexShrink: 0, textAlign: 'right', fontSize: 9 },
    cStockBms: { width: '10%', flexShrink: 0, textAlign: 'right', fontSize: 9 },
    footer: { position: 'absolute', bottom: 30, left: 36, right: 36, fontSize: 8, color: '#888', textAlign: 'center', borderTopWidth: 0.5, borderTopColor: '#ddd', borderTopStyle: 'solid', paddingTop: 8 },
  })
}

interface Props {
  matriz: Company
  filialName: string
  items: Pick<SeparationListItem, 'sku' | 'product_name' | 'quantity' | 'priority' | 'current_stock' | 'matriz_stock'>[]
  createdAt?: string
  notes?: string | null
}

function SeparationPDF({ matriz, filialName, items, createdAt, notes }: Props) {
  const primaryColor = matriz.primary_color ?? '#647B45'
  const styles = makeStyles(primaryColor)
  const logoSrc = matriz.logo_url ?? null
  const date = createdAt ?? new Date().toISOString()
  const totalQty = items.reduce((s, i) => s + Number(i.quantity), 0)

  return (
    <Document>
      <Page size="A4" style={styles.page}>
        <View style={styles.header}>
          <View style={{ flexDirection: 'row', alignItems: 'center' }}>
            {logoSrc && (
              <Image src={logoSrc} style={{ width: 48, height: 48, marginRight: 12, objectFit: 'contain' }} />
            )}
            <View>
              <Text style={styles.brand}>{matriz.name}</Text>
              <Text style={styles.subtitle}>Lista de Separação · Abastecimento de Filial · ax-kanban</Text>
            </View>
          </View>
        </View>

        <View style={styles.meta}>
          <View style={styles.metaCol}>
            <Text style={styles.metaLabel}>Filial destino</Text>
            <Text style={styles.metaValue}>{filialName}</Text>
          </View>
          <View style={styles.metaCol}>
            <Text style={styles.metaLabel}>Data</Text>
            <Text style={styles.metaValue}>{fmtDate(date)}</Text>
          </View>
          <View style={styles.metaCol}>
            <Text style={styles.metaLabel}>Itens / Total</Text>
            <Text style={styles.metaValue}>{items.length} itens · {totalQty} un</Text>
          </View>
        </View>

        <View style={styles.table}>
          <View style={styles.rowHead}>
            <Text style={[styles.cCheck, styles.headText]}>OK</Text>
            <Text style={[styles.cSku, styles.headText]}>SKU</Text>
            <Text style={[styles.cName, styles.headText]}>Produto</Text>
            <Text style={[styles.cQty, styles.headText]}>Qtd</Text>
            <Text style={[styles.cPrio, styles.headText]}>Prioridade</Text>
            <Text style={[styles.cStock, styles.headText]}>Estq Fil</Text>
            <Text style={[styles.cStockBms, styles.headText]}>Estq BMS</Text>
          </View>
          {items.map((item, idx) => (
            <View key={`${item.sku}-${idx}`} style={[styles.row, ...(idx % 2 === 1 ? [styles.rowAlt] : [])]}>
              <Text style={styles.cCheck}>[ ]</Text>
              <Text style={styles.cSku}>{item.sku}</Text>
              <Text style={styles.cName}>{item.product_name}</Text>
              <Text style={styles.cQty}>{item.quantity}</Text>
              <Text style={styles.cPrio}>{item.priority ?? '—'}</Text>
              <Text style={styles.cStock}>{item.current_stock ?? '—'}</Text>
              <Text style={styles.cStockBms}>{item.matriz_stock ?? '—'}</Text>
            </View>
          ))}
        </View>

        {notes && (
          <View style={{ marginTop: 18 }}>
            <Text style={styles.metaLabel}>Observações</Text>
            <Text style={{ marginTop: 4 }}>{notes}</Text>
          </View>
        )}

        <Text style={styles.footer}>Gerado por ax-kanban em {fmtDate(new Date().toISOString())}</Text>
      </Page>
    </Document>
  )
}

export async function generateSeparationPdfBlob(props: Props): Promise<Blob> {
  return await pdf(<SeparationPDF {...props} />).toBlob()
}

export default SeparationPDF
